/**
 * 發音檢查核心 — 把學生念的英文(語音辨識逐字稿)跟目標句子比對,算出涵蓋率
 * 與「哪些字有念到 / 漏了」,給出評分。
 *
 * 設計取捨(boss 2026-06-09「也要收音檢查」):
 * - **純前端、免後端、免 API**:用瀏覽器原生 Web Speech API(SpeechRecognition)
 *   做英文語音辨識,拿到逐字稿後在本檔做比對評分。不打任何網路 / claude bridge。
 * - 不支援的瀏覽器自動降級:只保留「錄音回放」自我檢查(見 oral-exam.ts)。
 * - 比對對 ASR 誤差寬容:正規化大小寫 / 標點後,字長 ≥4 允許 1 個編輯距離,避免
 *   小辨識誤差就算錯,挫折小孩。
 *
 * 本檔的「比對 / 評分」是純函式(好測);瀏覽器 API 包裝集中在底部,測試端可 stub。
 */

/** 一次發音檢查的結果。 */
export interface PronScore {
  /** 涵蓋率 0..1 = 目標字裡被念到的比例。 */
  coverage: number;
  /** 念到的目標字(原樣保留大小寫)。 */
  matched: string[];
  /** 漏掉 / 沒對上的目標字。 */
  missed: string[];
  /** 辨識到的逐字稿(正規化後的字)。 */
  transcriptWords: string[];
}

/** 評分等第(對齊紙本口試卷:優秀 / 良好 / 加油 / 需多練習)。 */
export interface PronRating {
  /** 等第標籤(中文)。 */
  label: string;
  /** 對應色票 token 名(theme.ts 的 --ef-*),由 UI 取用。 */
  tone: 'good' | 'ok' | 'warn';
  /** 0..100 百分數(coverage 取整)。 */
  percent: number;
}

/**
 * 正規化成可比對的字陣列:轉小寫、去標點(保留字母 / 數字 / 撇號)、合併空白。
 * 撇號保留以便 `can't`、`it's` 之類縮寫整體比對。
 */
export function normalizeWords(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter((w) => w.length > 0);
}

/** Levenshtein 編輯距離(小字串,iterative two-row)。 */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * 兩個字算不算「念對」:完全相同,或(字長 ≥4 時)編輯距離 ≤1 —— 對 ASR 的小誤差
 * 寬容,避免一個音差就判錯。
 */
export function wordMatches(expected: string, heard: string): boolean {
  if (expected === heard) return true;
  if (expected.length >= 4 && heard.length >= 4) {
    return editDistance(expected, heard) <= 1;
  }
  return false;
}

/**
 * 把逐字稿跟目標句比對。對每個目標字,在逐字稿(多重集合,可重複)裡找一個還沒
 * 被用掉的相符字;找到算 matched,否則 missed。涵蓋率 = matched / 目標字數。
 *
 * 用多重集合而非單純 includes,讓 `Run Run Run` 這種重複字要念到對應次數才算滿。
 */
export function scorePronunciation(expected: string, transcript: string): PronScore {
  const expectedWords = normalizeWords(expected);
  const transcriptWords = normalizeWords(transcript);

  // 逐字稿可用次數池(被對上就移除一個)。
  const pool = [...transcriptWords];
  const matched: string[] = [];
  const missed: string[] = [];

  for (const ew of expectedWords) {
    const idx = pool.findIndex((hw) => wordMatches(ew, hw));
    if (idx >= 0) {
      matched.push(ew);
      pool.splice(idx, 1);
    } else {
      missed.push(ew);
    }
  }

  const total = expectedWords.length;
  const coverage = total === 0 ? 0 : matched.length / total;
  return { coverage, matched, missed, transcriptWords };
}

/** 由涵蓋率給等第(≥0.85 優秀 / ≥0.7 良好 / ≥0.5 加油 / 否則需多練習)。 */
export function ratePronunciation(coverage: number): PronRating {
  const percent = Math.round(coverage * 100);
  if (coverage >= 0.85) return { label: '優秀', tone: 'good', percent };
  if (coverage >= 0.7) return { label: '良好', tone: 'good', percent };
  if (coverage >= 0.5) return { label: '加油', tone: 'ok', percent };
  return { label: '需多練習', tone: 'warn', percent };
}

// ────────────────────────────────────────────────────────────────────────
// 瀏覽器 API 包裝(非純函式;測試端可用 vi.stubGlobal 覆蓋 window 屬性)
// ────────────────────────────────────────────────────────────────────────

/** 取得 SpeechRecognition 建構子(Chrome/Edge 為 webkit 前綴);沒有回 null。 */
export function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** 此瀏覽器是否支援自動發音檢查(語音辨識)。 */
export function isSpeechCheckSupported(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

/** 此瀏覽器是否支援錄音(getUserMedia + MediaRecorder)。 */
export function isRecordingSupported(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const hasMedia = !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';
  const hasRecorder = typeof (window as unknown as { MediaRecorder?: unknown }).MediaRecorder === 'function';
  return hasMedia && hasRecorder;
}

/** SpeechRecognition 的最小型別(避免依賴各環境 lib.dom 差異)。 */
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

/** SpeechRecognition result 事件的最小型別。 */
export interface SpeechRecognitionResultEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string; confidence?: number }>>;
}

/** 一次語音辨識的控制把手。 */
export interface RecognitionHandle {
  /** 提早停止辨識(會觸發 resolve)。 */
  stop(): void;
}

/**
 * 跑一次英文語音辨識,把(累積的)逐字稿透過 callback 回報。
 *
 * - `onPartial`:每段辨識結果即時回報(可即時顯示)。
 * - 回傳的 Promise 在 `onend`(辨識自然結束 / 被 stop)時 resolve 成完整逐字稿。
 * - 不支援時 reject。
 */
export function recognizeSpeech(
  lang: string,
  onPartial?: (transcript: string) => void,
): { promise: Promise<string>; handle: RecognitionHandle } {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) {
    return {
      promise: Promise.reject(new Error('speech-recognition-unsupported')),
      handle: { stop: () => {} },
    };
  }

  const rec = new Ctor();
  rec.lang = lang;
  rec.continuous = true;
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  let finalText = '';

  const promise = new Promise<string>((resolve, reject) => {
    rec.onresult = (event) => {
      let interim = '';
      const results = event.results;
      for (let i = 0; i < results.length; i++) {
        const alt = results[i]?.[0];
        if (alt) interim += alt.transcript + ' ';
      }
      finalText = interim.trim();
      onPartial?.(finalText);
    };
    rec.onerror = (event) => {
      // no-speech / aborted 視為正常結束(回目前累積結果),其餘 reject。
      const err = event?.error;
      if (err === 'no-speech' || err === 'aborted') {
        resolve(finalText);
      } else {
        reject(new Error(`speech-recognition-error:${err ?? 'unknown'}`));
      }
    };
    rec.onend = () => resolve(finalText);
    try {
      rec.start();
    } catch (e) {
      reject(e instanceof Error ? e : new Error('speech-recognition-start-failed'));
    }
  });

  return { promise, handle: { stop: () => rec.stop() } };
}

/** 錄音控制把手。 */
export interface RecorderHandle {
  /** 停止錄音,回傳音檔 Blob + 可播放的 object URL。 */
  stop(): Promise<{ blob: Blob; url: string }>;
}

/**
 * 開始錄音(getUserMedia + MediaRecorder)。回傳 handle,呼叫 `stop()` 取得音檔。
 * stop 後會關閉麥克風 track。不支援時 reject。
 */
export async function startRecording(): Promise<RecorderHandle> {
  if (!isRecordingSupported()) throw new Error('recording-unsupported');

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const MediaRec = (window as unknown as { MediaRecorder: typeof MediaRecorder }).MediaRecorder;
  const recorder = new MediaRec(stream);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e: BlobEvent) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  recorder.start();

  return {
    stop: () =>
      new Promise<{ blob: Blob; url: string }>((resolve) => {
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
          const url = URL.createObjectURL(blob);
          resolve({ blob, url });
        };
        recorder.stop();
      }),
  };
}
